import React, { useState, useEffect, useRef } from 'react';
import { UnterweisungenLayout } from './UnterweisungenLayout';
import { Card, CardContent } from '../../components/ui/card';
import { FileText, Loader2, Upload, X, Camera, File, Check, AlertCircle, ChevronDown, MapPin, Edit, Trash2, Send, PenLine } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ResendInstructionModal } from '../../components/ResendInstructionModal';
import { InstructionSignModal } from '../../components/InstructionSignModal';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const UnterweisungenErstellen = () => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [existingCategories, setExistingCategories] = useState([]);
  const [instructions, setInstructions] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    file: null,
    filePreview: null,
    fileType: 'pdf',
    fileName: '',
    selectedEmployees: [],
    selectedLocations: [],
    deadline: '',
    unterwiesen_am_bulk: '',  // "Persönlich unterwiesen am" – gilt für alle ausgewählten MA
    recurrence_enabled: false,
    recurrence_type: '',
    auto_send_to_new_employees: false
  });
  
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('all');
  const [editingInstruction, setEditingInstruction] = useState(null);
  const [resendModal, setResendModal] = useState(null);
  const [slSignModal, setSlSignModal] = useState(null); // { instruction }
  const [slName, setSlName] = useState('');

  useEffect(() => {
    fetchData();
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const p = JSON.parse(atob(token.split('.')[1]));
        setSlName(p.name || p.sub || '');
      } catch { /* silent */ }
    }
  }, []);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [employeesRes, locationsRes, categoriesRes, instructionsRes] = await Promise.all([
        axios.get(`${API}/api/admin/employees`, { headers }),
        axios.get(`${API}/api/admin/locations`, { headers }),
        axios.get(`${API}/api/instructions/categories`, { headers }),
        axios.get(`${API}/api/instructions`, { headers })
      ]);
      
      setEmployees(employeesRes.data || []);
      setLocations(locationsRes.data || []);
      setExistingCategories(categoriesRes.data?.categories || []);
      setInstructions(instructionsRes.data || []);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Datei zu groß. Maximal 10MB erlaubt.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      let fileType = 'pdf';
      
      // Auto-detect file type
      if (file.type.startsWith('image/')) {
        fileType = 'image';
        
        // If type is 'scan' or 'auto' and it's an image, process it
        if (type === 'scan' || type === 'auto') {
          processScannedImage(base64, file.name);
          return;
        }
      }
      
      setFormData(prev => ({
        ...prev,
        file: base64,
        filePreview: fileType === 'image' ? base64 : null,
        fileType,
        fileName: file.name
      }));
      
      if (fileType === 'image') {
        toast.success('📸 Foto hochgeladen');
      } else {
        toast.success('📄 PDF hochgeladen');
      }
    };
    reader.readAsDataURL(file);
  };

  const processScannedImage = (base64, fileName) => {
    // Create an image element to process
    const img = new Image();
    img.onload = () => {
      // Create canvas for processing
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size to image size
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw image
      ctx.drawImage(img, 0, 0);
      
      // Get image data for processing
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Simple auto-contrast enhancement
      let min = 255, max = 0;
      for (let i = 0; i < data.length; i += 4) {
        const gray = (data[i] + data[i + 1] + data[i + 2]) / 3;
        if (gray < min) min = gray;
        if (gray > max) max = gray;
      }
      
      const range = max - min;
      if (range > 0) {
        for (let i = 0; i < data.length; i += 4) {
          data[i] = ((data[i] - min) / range) * 255;
          data[i + 1] = ((data[i + 1] - min) / range) * 255;
          data[i + 2] = ((data[i + 2] - min) / range) * 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      
      // Convert to base64
      const processedBase64 = canvas.toDataURL('image/jpeg', 0.9);
      
      setFormData(prev => ({
        ...prev,
        file: processedBase64,
        filePreview: processedBase64,
        fileType: 'image',
        fileName: fileName.replace(/\.[^/.]+$/, '') + '_scan.jpg'
      }));
      
      toast.success('📄 Dokument gescannt und optimiert!');
    };
    
    img.src = base64;
  };

  const toggleEmployee = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      selectedEmployees: prev.selectedEmployees.includes(employeeId)
        ? prev.selectedEmployees.filter(id => id !== employeeId)
        : [...prev.selectedEmployees, employeeId]
    }));
  };

  const toggleLocation = (locationId) => {
    setFormData(prev => ({
      ...prev,
      selectedLocations: prev.selectedLocations.includes(locationId)
        ? prev.selectedLocations.filter(id => id !== locationId)
        : [...prev.selectedLocations, locationId]
    }));
  };

  const selectAllEmployeesByLocation = (locationId) => {
    const locationEmployees = employees.filter(emp => 
      emp.location_ids && emp.location_ids.includes(locationId)
    );
    const employeeIds = locationEmployees.map(emp => emp.id);
    
    setFormData(prev => ({
      ...prev,
      selectedEmployees: [...new Set([...prev.selectedEmployees, ...employeeIds])]
    }));
    toast.success(`${employeeIds.length} Mitarbeiter ausgewählt`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error('Bitte Titel eingeben');
      return;
    }
    if (!formData.category.trim()) {
      toast.error('Bitte Kategorie eingeben');
      return;
    }
    if (!formData.file) {
      toast.error('Bitte Datei hochladen');
      return;
    }
    if (formData.selectedEmployees.length === 0 && formData.selectedLocations.length === 0) {
      toast.error('Bitte mindestens einen Mitarbeiter oder Standort auswählen');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        file_url: formData.file,
        file_type: formData.fileType,
        file_name: formData.fileName,
        assigned_to: formData.selectedEmployees,
        location_ids: formData.selectedLocations,
        deadline: formData.deadline || null,
        unterwiesen_am_bulk: formData.unterwiesen_am_bulk || null,
        recurrence_enabled: formData.recurrence_enabled,
        recurrence_type: formData.recurrence_enabled ? formData.recurrence_type : null,
        auto_send_to_new_employees: formData.auto_send_to_new_employees,
        customer_id: '' // Backend überschreibt diesen Wert
      };
      
      if (editingInstruction) {
        // Update existing
        await axios.put(`${API}/api/instructions/${editingInstruction.id}`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Unterweisung aktualisiert!');
      } else {
        // Create new
        const response = await axios.post(`${API}/api/instructions`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const newInstruction = response.data;
        toast.success('Unterweisung erfolgreich erstellt und versendet!');
        
        // Reset form
        setFormData({
          title: '',
          description: '',
          category: '',
          file: null,
          filePreview: null,
          fileType: 'pdf',
          fileName: '',
          selectedEmployees: [],
          selectedLocations: [],
          deadline: '',
          unterwiesen_am_bulk: '',
          recurrence_enabled: false,
          recurrence_type: '',
          auto_send_to_new_employees: false
        });
        setShowForm(false);
        setEditingInstruction(null);
        fetchData();
        
        // Unterschrift-Modal direkt nach dem Erstellen öffnen
        setSlSignModal({ instruction: newInstruction });
        return; // frühzeitig return um doppeltes reset zu vermeiden
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern der Unterweisung');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (instruction) => {
    // Fetch full instruction detail (including file_url which is excluded from list)
    try {
      const token = localStorage.getItem('token');
      const detailRes = await axios.get(`${API}/api/instructions/${instruction.id}/detail`, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 15000
      });
      const fullInstruction = detailRes.data;
      setEditingInstruction(fullInstruction);
      setFormData({
        title: fullInstruction.title,
        description: fullInstruction.description || '',
        category: fullInstruction.category,
        file: fullInstruction.file_url,
        filePreview: fullInstruction.file_type === 'image' ? fullInstruction.file_url : null,
        fileType: fullInstruction.file_type,
        fileName: fullInstruction.file_name,
        selectedEmployees: fullInstruction.assigned_to || [],
        selectedLocations: fullInstruction.location_ids || [],
        deadline: fullInstruction.deadline || '',
        recurrence_enabled: fullInstruction.recurrence_enabled || false,
        recurrence_type: fullInstruction.recurrence_type || '',
        auto_send_to_new_employees: fullInstruction.auto_send_to_new_employees || false
      });
    } catch (error) {
      console.error('Fehler beim Laden der Unterweisung:', error);
      toast.error('Unterweisung konnte nicht geladen werden');
      return;
    }
    setShowForm(true);
  };

  const handleDelete = async (instructionId) => {
    if (!window.confirm('Möchten Sie diese Unterweisung wirklich löschen?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/instructions/${instructionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Unterweisung gelöscht');
      fetchData();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      toast.error('Fehler beim Löschen der Unterweisung');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = selectedLocationFilter === 'all' 
    ? employees 
    : employees.filter(emp => emp.location_ids && emp.location_ids.includes(selectedLocationFilter));

  if (loading && employees.length === 0) {
    return (
      <UnterweisungenLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </UnterweisungenLayout>
    );
  }

  return (
    <UnterweisungenLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Unterweisungen</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {instructions.length} Unterweisung{instructions.length !== 1 ? 'en' : ''} erstellt
            </p>
          </div>
          {!showForm && (
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
            >
              <Upload className="mr-2 h-4 w-4" />
              Neue Unterweisung
            </Button>
          )}
        </div>

        {/* Create Form */}
        {showForm && (
          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Titel <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="z.B. HACCP Schulung Januar 2025"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Category with Autocomplete */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kategorie <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => {
                      setFormData({...formData, category: e.target.value});
                      setShowCategoryDropdown(true);
                    }}
                    onFocus={() => setShowCategoryDropdown(true)}
                    placeholder="z.B. Safety Moment, HACCP, Hygiene"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  
                  {/* Autocomplete Dropdown */}
                  {showCategoryDropdown && existingCategories.length > 0 && formData.category && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {existingCategories
                        .filter(cat => cat.toLowerCase().includes(formData.category.toLowerCase()))
                        .map((cat, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setFormData({...formData, category: cat});
                              setShowCategoryDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm"
                          >
                            {cat}
                          </button>
                        ))}
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Beschreibung (optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Zusätzliche Informationen..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Deadline */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Frist bis (optional)
                  </label>
                  <input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {formData.deadline && (
                    <p className="text-xs text-gray-500 mt-1">
                      Mitarbeiter müssen bis zum {new Date(formData.deadline).toLocaleDateString('de-DE')} lesen
                    </p>
                  )}
                </div>

                {/* Persönlich unterwiesen am (optional, gilt für alle) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Persönlich unterwiesen am <span className="font-normal text-gray-500">(optional – gilt für alle ausgewählten Mitarbeiter)</span>
                  </label>
                  <input
                    type="date"
                    value={formData.unterwiesen_am_bulk}
                    onChange={(e) => setFormData({...formData, unterwiesen_am_bulk: e.target.value})}
                    data-testid="unterwiesen-am-bulk-input"
                    className="w-full px-4 py-2 border border-blue-300 dark:border-blue-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {formData.unterwiesen_am_bulk && (
                    <p className="text-xs text-blue-600 mt-1">
                      Alle zugewiesenen Mitarbeiter werden mit dem Datum {new Date(formData.unterwiesen_am_bulk + 'T12:00:00').toLocaleDateString('de-DE')} als "Persönlich unterwiesen" voreingetragen. Das Datum kann nachher pro Mitarbeiter angepasst werden.
                    </p>
                  )}
                </div>

                {/* Wiederholung */}
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="recurrence_enabled"
                      checked={formData.recurrence_enabled}
                      onChange={(e) => setFormData({...formData, recurrence_enabled: e.target.checked, recurrence_type: e.target.checked ? 'monthly' : ''})}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                    />
                    <label htmlFor="recurrence_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      🔄 Wiederkehrende Unterweisung
                    </label>
                  </div>
                  
                  {formData.recurrence_enabled && (
                    <div className="ml-6 space-y-2">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2">Wiederholungsintervall</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {[
                          { value: 'biweekly', label: 'Alle 2 Wochen' },
                          { value: 'weekly', label: 'Wöchentlich' },
                          { value: 'monthly', label: 'Monatlich' },
                          { value: 'quarterly', label: 'Quartalsweise' },
                          { value: 'yearly', label: 'Jährlich' }
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setFormData({...formData, recurrence_type: option.value})}
                            className={`px-2 py-2 text-xs sm:text-sm rounded-lg border-2 transition-all ${
                              formData.recurrence_type === option.value
                                ? 'border-blue-600 bg-blue-50 text-blue-700 font-medium'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        ℹ️ Die Unterweisung wird automatisch an aktive Mitarbeiter versendet
                      </p>
                    </div>
                  )}
                </div>

                {/* Auto-send to new employees */}
                <div className="border-t pt-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.auto_send_to_new_employees}
                      onChange={(e) => setFormData({...formData, auto_send_to_new_employees: e.target.checked})}
                      className="h-5 w-5 text-blue-600 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        🆕 Automatisch an neue Mitarbeiter senden
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        Diese Unterweisung wird automatisch an jeden neuen Mitarbeiter versendet, sobald er registriert wird.
                      </p>
                    </div>
                  </label>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Dokument <span className="text-red-500">*</span>
                  </label>
                  
                  {!formData.file ? (
                    <>
                      {/* Single Upload Button - Opens Native Picker */}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full flex items-center justify-center gap-3 p-4 sm:p-6 border-2 border-dashed border-blue-500 rounded-lg hover:bg-blue-50 transition-all bg-white dark:bg-gray-900"
                      >
                        <Upload className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
                        <div className="text-center">
                          <span className="text-base sm:text-lg font-semibold text-blue-600">Dokument hochladen</span>
                          <p className="text-xs sm:text-sm text-gray-500 mt-1">PDF, Foto oder Kamera</p>
                        </div>
                      </button>

                      {/* Native File Input - NO capture attribute for full mobile options */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => handleFileUpload(e, 'auto')}
                        className="hidden"
                      />
                    </>
                  ) : (
                    <div className="relative border-2 border-green-500 rounded-lg p-4 bg-green-50">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, file: null, filePreview: null, fileName: ''})}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      
                      {formData.filePreview ? (
                        <img src={formData.filePreview} alt="Preview" className="w-full h-48 object-contain rounded" />
                      ) : (
                        <div className="flex items-center gap-3">
                          <File className="h-12 w-12 text-green-600" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{formData.fileName}</p>
                            <p className="text-xs text-gray-500">Hochgeladen</p>
                          </div>
                          <Check className="h-6 w-6 text-green-600" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Location Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Standorte auswählen (optional)
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    {locations.map(location => (
                      <label key={location.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:bg-gray-800 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={formData.selectedLocations.includes(location.id)}
                          onChange={() => toggleLocation(location.id)}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{location.name}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.preventDefault();
                            selectAllEmployeesByLocation(location.id);
                          }}
                          className="ml-auto text-xs"
                        >
                          Alle MA zuweisen
                        </Button>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Employee Selection with Location Filter */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Mitarbeiter auswählen
                    </label>
                    <select
                      value={selectedLocationFilter}
                      onChange={(e) => setSelectedLocationFilter(e.target.value)}
                      className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1"
                    >
                      <option value="all">Alle Standorte</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    {filteredEmployees.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Keine Mitarbeiter gefunden
                      </p>
                    ) : (
                      filteredEmployees.map(employee => {
                        const empLocations = locations.filter(loc => 
                          employee.location_ids?.includes(loc.id)
                        );
                        
                        return (
                          <label 
                            key={employee.id} 
                            className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 dark:bg-gray-800 p-2 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={formData.selectedEmployees.includes(employee.id)}
                              onChange={() => toggleEmployee(employee.id)}
                              className="h-4 w-4 text-blue-600 rounded mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{employee.name}</p>
                              <p className="text-xs text-gray-500">{employee.email}</p>
                              {empLocations.length > 0 && (
                                <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                                  <MapPin className="h-3 w-3" />
                                  {empLocations.map(l => l.name).join(', ')}
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                  
                  {formData.selectedEmployees.length > 0 && (
                    <p className="text-sm text-blue-600 mt-2">
                      {formData.selectedEmployees.length} Mitarbeiter ausgewählt
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    disabled={loading}
                    className="flex-1"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {editingInstruction ? 'Speichere...' : 'Erstelle...'}
                      </>
                    ) : (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        {editingInstruction ? 'Aktualisieren' : 'Erstellen'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Instructions List */}
        {!showForm && instructions.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Erstellte Unterweisungen</h3>
            {instructions.map(instruction => (
              <Card key={instruction.id}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
                      <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 flex-wrap">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base">{instruction.title}</h4>
                        {instruction.recurrence_enabled && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium whitespace-nowrap">
                            🔄 {
                              instruction.recurrence_type === 'biweekly' ? 'Alle 2 Wochen' :
                              instruction.recurrence_type === 'weekly' ? 'Wöchentlich' :
                              instruction.recurrence_type === 'monthly' ? 'Monatlich' :
                              instruction.recurrence_type === 'quarterly' ? 'Quartalsweise' :
                              instruction.recurrence_type === 'yearly' ? 'Jährlich' : 'Wiederkehrend'
                            }
                          </span>
                        )}
                        {instruction.auto_send_to_new_employees && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium whitespace-nowrap">
                            🆕 Auto-Versand
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                          {instruction.category}
                        </span>
                        {instruction.deadline && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            📅 Frist: {new Date(instruction.deadline).toLocaleDateString('de-DE')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {instruction.assigned_to?.length || 0} Mitarbeiter • 
                        {instruction.location_ids?.length || 0} Standorte
                      </div>
                      {instruction.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">{instruction.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        Erstellt: {new Date(instruction.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setResendModal(instruction)}
                        className="h-8 px-2 text-xs"
                        title="Erneut versenden"
                      >
                        <Send className="h-4 w-4 text-green-600" />
                        <span className="hidden sm:inline ml-1">Versenden</span>
                      </Button>
                      {/* SL rückwirkend unterschreiben */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSlSignModal({ instruction })}
                        className={`h-8 px-2 text-xs ${instruction.signature_sl ? 'text-blue-600' : 'text-gray-400'}`}
                        title={instruction.signature_sl ? 'Bereits unterschrieben – erneut unterschreiben' : 'Unterschreiben'}
                        data-testid={`sl-sign-btn-${instruction.id}`}
                      >
                        <PenLine className="h-4 w-4" />
                        <span className="hidden sm:inline ml-1">{instruction.signature_sl ? 'Unterschrieben' : 'Unterschreiben'}</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(instruction)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4 text-blue-600" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(instruction.id)}
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!showForm && instructions.length === 0 && (
          <Card>
            <CardContent className="py-16">
              <div className="text-center">
                <div className="p-4 bg-blue-100 rounded-full mb-4 inline-block">
                  <FileText className="h-12 w-12 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Noch keine Unterweisungen</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Erstellen Sie Ihre erste Unterweisung für Ihre Mitarbeiter
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Resend Modal */}
      {resendModal && (
        <ResendInstructionModal
          instruction={resendModal}
          onClose={() => setResendModal(null)}
          onSuccess={() => {
            const inst = resendModal; // vor dem Schließen sichern
            setResendModal(null);
            fetchData();
            // Unterschrift-Modal nach dem Versenden öffnen
            setSlSignModal({ instruction: inst });
          }}
        />
      )}

      {/* SL rückwirkend unterschreiben */}
      {slSignModal && (
        <InstructionSignModal
          open={!!slSignModal}
          onClose={() => setSlSignModal(null)}
          title={slSignModal.instruction?.title}
          signerLabel="Stationsleitung / Admin"
          signerName={slName}
          confirmLabel="Unterschrift speichern"
          isEmployee={false}
          onSign={async (sig) => {
            const token = localStorage.getItem('token');
            await axios.put(
              `${API}/api/instructions/${slSignModal.instruction.id}/sign-sl`,
              { signature_sl: sig },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Unterschrift gespeichert');
            setSlSignModal(null);
            fetchData();
          }}
        />
      )}
    </UnterweisungenLayout>
  );
};
